import { formatNumber } from '../format'
import i18n from './i18n'
import type { Amount, Locales, ProjectAction } from '@captaincause/types'

type ComputeImpactProps = {
  amount: Amount | undefined
  action: ProjectAction | undefined
}

export function floorImpactItems(items: number) {
  return items > 1 ? Math.floor(items) : Math.floor(items * 100) / 100
}

export function computeImpact({ amount, action }: ComputeImpactProps) {
  if (!amount || !action) return null

  const emptyAction = !action?.cost.amount || !action?.num_items

  if (emptyAction) return null

  const impact = (amount.amount / action.cost.amount) * action.num_items

  if (
    action.description.startsWith('euros') ||
    action.description.startsWith('€')
  ) {
    return impact
  }

  return floorImpactItems(impact)
}

export function actionDescription({
  action,
  numItems = undefined,
  length = 'short',
}: {
  action: ProjectAction | undefined
  numItems?: number
  length?: 'base' | 'long' | 'short'
}): string | null {
  if (!action) return null

  const numberOfItems = numItems ?? action.num_items

  const isPlural = numberOfItems > 1

  if (action.description_v2) {
    if (length === 'base') {
      return action.description_v2.base
    }

    return (
      action.description_v2[length]?.[isPlural ? 'plural' : 'singular'] ||
      action.description_v2.base
    )
  }

  const pluralizedDescription = isPlural
    ? action.description_plural
    : action.description_singular

  return pluralizedDescription || action.description
}

export function displayImpact({
  action,
  value,
  fallback = undefined,
  length = 'short',
  locale,
}: {
  action: ProjectAction | undefined
  value: { numItems: number } | { amount: Amount }
  fallback?: string
  length?: 'base' | 'long' | 'short'
  locale: Locales
}) {
  if (!action) return fallback

  const numItems =
    'numItems' in value
      ? value.numItems
      : computeImpact({ amount: value.amount, action })
  if (!numItems) return fallback

  const description = actionDescription({ action, numItems, length })

  return description
    ? `${formatNumber(numItems, locale)} ${description}`
    : fallback
}

// TODO: let's think about refactor this in regards to the existing `displayImpact` function
export function displayImpactWithFundOrContribute({
  action,
  value,
  fallback = undefined,
  length = 'short',
  locale,
}: {
  action: ProjectAction | undefined
  value: { numItems: number } | { amount: Amount }
  fallback?: string
  length?: 'base' | 'long' | 'short'
  locale: Locales
}) {
  if (!action) return fallback

  const numItems =
    'numItems' in value
      ? value.numItems
      : computeImpact({ amount: value.amount, action })

  if (numItems === null) return fallback

  const description = actionDescription({ action, numItems, length })

  if (!description) return fallback

  if (numItems < 1 && description) {
    return `${i18n[locale].contribute} 1 ${description}`
  }

  return `${i18n[locale].fund} ${formatNumber(numItems, locale)} ${description}`
}
